
import { NextResponse } from 'next/server';
import { supabase } from '@/lib/supabase';
import { BrandExtractorService } from '@/lib/services/brand-extractor.service';
import fs from 'fs';
import path from 'path';

export const maxDuration = 300; // 5 minutes

// This endpoint is triggered by the client (for prototype) to run the extraction
export async function POST(req) {
    try {
        const { jobId } = await req.json();

        if (!jobId) {
            return NextResponse.json({ error: 'Missing jobId' }, { status: 400 });
        }

        // 1. Update status to 'processing'
        await supabase
            .from('scan_jobs')
            .update({ status: 'processing', progress_pages: 0 })
            .eq('id', jobId);

        // 2. Fetch job details
        const { data: job, error: jobError } = await supabase
            .from('scan_jobs')
            .select('*')
            .eq('id', jobId)
            .single();

        if (jobError || !job) {
            throw new Error('Job not found');
        }

        // 3. Download PDF from Storage
        const { data: pdfData, error: downloadError } = await supabase
            .storage
            .from('scans')
            .download(job.storage_path);

        if (downloadError) {
            throw new Error('Failed to download PDF: ' + downloadError.message);
        }

        // Save to temp file for processing
        const arrayBuffer = await pdfData.arrayBuffer();
        const buffer = Buffer.from(arrayBuffer);
        const tempPdfPath = path.join(process.cwd(), `temp_${jobId}.pdf`);
        fs.writeFileSync(tempPdfPath, buffer);

        try {
            // 4. Initialize Service
            const service = new BrandExtractorService();
            const totalPages = await service.getPageCount(tempPdfPath);

            // Update total pages
            await supabase
                .from('scan_jobs')
                .update({ total_pages: totalPages })
                .eq('id', jobId);

            console.log(`Job ${jobId}: Processing ${totalPages} pages`);

            let extractedCount = 0;
            let lastError = null;

            // 5. Loop through pages
            for (let page = 1; page <= totalPages; page++) {
                try {
                    console.log(`Job ${jobId}: extracting page ${page}`);
                    const result = await service.extract(tempPdfPath, page);

                    // Debug: Log what Gemini returned
                    console.log(`Page ${page} Gemini result:`, JSON.stringify(result, null, 2));

                    if (result && result.brandName && result.brandName.trim()) {
                        extractedCount++;
                        console.log(`✓ Extracted brand: ${result.brandName}`);

                        // Insert result
                        const { data, error: insertError } = await supabase
                            .from('pdf_brands')
                            .insert({
                                job_id: jobId,
                                name: result.brandName,
                                normalized_name: result.brandName.toLowerCase().trim(),
                                pages: [page],
                                logo_path: result.logoUrl,
                                nice_class: Array.isArray(result.niceClassification) ? result.niceClassification.join(', ') : result.niceClassification,
                                application_number: result.applicationNumber,
                                filing_date: result.filingDate,
                                expiration_date: result.expiryDate,
                                owner: result.owner,
                                colors: result.colors,
                                description: result.description
                            });

                        if (insertError) {
                            console.error(`❌ Database insert error for ${result.brandName}:`, insertError);
                            throw insertError;
                        } else {
                            console.log(`✅ Successfully saved ${result.brandName} to database`);
                        }
                    } else {
                        console.log(`✗ Page ${page}: No brand name found or empty response`);
                    }

                } catch (pageError) {
                    console.error(`Page ${page} Error:`, pageError);
                    lastError = pageError.message;
                    // Continue to next page
                }

                // Update Progress
                await supabase
                    .from('scan_jobs')
                    .update({ progress_pages: page })
                    .eq('id', jobId);
            }

            // 6. Finalize Job Status
            if (extractedCount === 0 && lastError) {
                // If we found nothing AND had errors, mark as failed
                await supabase
                    .from('scan_jobs')
                    .update({
                        status: 'failed',
                        error: `Extraction failed: ${lastError}`
                    })
                    .eq('id', jobId);
            } else {
                // Mark as completed (even if 0 found but no error, or partial success)
                await supabase
                    .from('scan_jobs')
                    .update({ status: 'completed' })
                    .eq('id', jobId);
            }

            return NextResponse.json({ success: true, count: extractedCount });

        } finally {
            // Cleanup temp file
            if (fs.existsSync(tempPdfPath)) {
                fs.unlinkSync(tempPdfPath);
            }
        }

    } catch (error) {
        console.error('Job Run Error:', error);

        // Try to update status if possible
        if (req.body) {
            try {
                // If we can't parse req, we fail silent on DB update
            } catch (e) { }
        }

        // Make best effort to fail the job in DB
        // We need jobId, but it might be lost if error happened early. 
        // Assuming we got it since the first thing we do is parse it.

        return NextResponse.json({ error: 'Job execution failed: ' + error.message }, { status: 500 });
    }
}
